import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardHeader,
    Checkbox,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import productService from '../../../services/productService';
import { useCurrency } from '../../../hooks/useCurrency';

/** Unit price after this promotion — null for BUY_X_GET_Y (depends on quantity). */
export const priceAfterPromotion = (price, promotion) => {
    const p = Number(price) || 0;
    const v = Number(promotion.value) || 0;
    if (promotion.type === 'PERCENTAGE') return Math.max(p - (p * v) / 100, 0);
    if (promotion.type === 'FIXED_AMOUNT') return Math.max(p - v, 0);
    return null;
};

/** A variant priced below the promotion's minimum price can't be part of it. */
export const isBelowMinimum = (variant, minimumAmount) =>
    minimumAmount !== '' && minimumAmount != null && Number(variant.price) < Number(minimumAmount);

const not = (a, b) => a.filter((x) => !b.includes(x));
const intersection = (a, b) => a.filter((x) => b.includes(x));
const union = (a, b) => [...a, ...not(b, a)];

/**
 * One side of the transfer list: header with "select all" checkbox + rows.
 * `items` are { id, productName, name, sku, price }.
 */
const TransferColumn = ({ title, items, checked, onToggle, onToggleAll, renderSecondary, emptyText, showProduct, isDisabled = () => false }) => {
    const { t } = useTranslation();
    // Disabled rows stay visible but can't be checked/moved.
    const selectable = items.filter((i) => !isDisabled(i));
    const checkedCount = intersection(checked, selectable.map((i) => i.id)).length;

    return (
        <Card variant="outlined" sx={{ flex: 1, minWidth: 0 }}>
            <CardHeader
                sx={{ px: 2, py: 1 }}
                avatar={
                    <Checkbox
                        onClick={onToggleAll}
                        checked={selectable.length > 0 && checkedCount === selectable.length}
                        indeterminate={checkedCount !== selectable.length && checkedCount !== 0}
                        disabled={selectable.length === 0}
                        inputProps={{ 'aria-label': t('promotions_ext.selectAll') }}
                    />
                }
                title={title}
                subheader={t('promotions_ext.checkedCount', { checked: checkedCount, total: selectable.length })}
            />
            <Divider />
            <List dense component="div" role="list" sx={{ height: 280, overflow: 'auto' }}>
                {items.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                        {emptyText}
                    </Typography>
                )}
                {items.map((v) => (
                    <ListItemButton key={v.id} role="listitem" disabled={isDisabled(v)} onClick={() => onToggle(v.id)}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                                edge="start"
                                size="small"
                                disabled={isDisabled(v)}
                                checked={checked.includes(v.id)}
                                tabIndex={-1}
                                disableRipple
                            />
                        </ListItemIcon>
                        <ListItemText
                            primary={showProduct ? `${v.productName} — ${v.name}` : v.name}
                            secondary={renderSecondary(v)}
                        />
                    </ListItemButton>
                ))}
            </List>
        </Card>
    );
};

/**
 * VARIANT-target picker built as a MUI "basic transfer list".
 * Search + pick a product → its variants appear on the left. Move them to the
 * right (the chosen variants, from any number of products). Pick another
 * product and repeat.
 *
 * `selectedVariants` items: { id, productId, productName, name, sku, price }.
 */
const PromotionVariantPicker = ({ products, selectedVariants, onChange, promotion, error }) => {
    const { t } = useTranslation();
    const { format: formatCurrency } = useCurrency();
    const [product, setProduct] = useState(null);
    const [productVariants, setProductVariants] = useState([]);
    const [checkedLeft, setCheckedLeft] = useState([]);
    const [checkedRight, setCheckedRight] = useState([]);

    useEffect(() => {
        setCheckedLeft([]);
        if (!product) {
            setProductVariants([]);
            return;
        }
        productService.getProductVariants(product.id)
            .then((data) => setProductVariants(data || []))
            .catch(() => setProductVariants([]));
    }, [product]);

    const selectedIds = useMemo(() => selectedVariants.map((v) => v.id), [selectedVariants]);

    // Left: the picked product's variants that aren't chosen yet.
    const left = useMemo(
        () => productVariants
            .filter((v) => !selectedIds.includes(v.id))
            .map((v) => ({
                id: v.id,
                productId: product.id,
                productName: product.name,
                name: v.name,
                sku: v.sku,
                price: v.price,
            })),
        [productVariants, selectedIds, product]
    );

    // Right: everything chosen so far, grouped by product for readability.
    const right = useMemo(
        () => [...selectedVariants].sort((a, b) =>
            (a.productName || '').localeCompare(b.productName || '') || (a.name || '').localeCompare(b.name || '')),
        [selectedVariants]
    );

    // Variants priced below the promotion's minimum are listed (greyed out)
    // but can never be checked or moved to the chosen list.
    const leftBlocked = (v) => isBelowMinimum(v, promotion.minimumAmount);
    const leftIds = left.filter((v) => !leftBlocked(v)).map((v) => v.id);
    const rightIds = right.map((v) => v.id);
    const leftChecked = intersection(checkedLeft, leftIds);
    const rightChecked = intersection(checkedRight, rightIds);

    const toggle = (setter) => (id) =>
        setter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const toggleAll = (checked, setter, ids) => () =>
        setter(intersection(checked, ids).length === ids.length ? not(checked, ids) : union(checked, ids));

    const moveRight = (ids) => {
        const moved = left.filter((v) => ids.includes(v.id) && !leftBlocked(v));
        if (moved.length) onChange([...selectedVariants, ...moved]);
        setCheckedLeft((prev) => not(prev, ids));
    };

    const moveLeft = (ids) => {
        onChange(selectedVariants.filter((v) => !ids.includes(v.id)));
        setCheckedRight((prev) => not(prev, ids));
    };

    const invalidCount = selectedVariants.filter((v) => isBelowMinimum(v, promotion.minimumAmount)).length;
    const buyXGetYLabel = `${promotion.buyQuantity || '?'}+${promotion.getQuantity || '?'} offert`;

    const leftSecondary = (v) => {
        const tooCheap = leftBlocked(v);
        return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {v.sku ? `${v.sku} · ` : ''}{formatCurrency(v.price)}
                {tooCheap && (
                    <>
                        <WarningIcon color="warning" sx={{ fontSize: 16 }} />
                        {t('promotions_ext.belowMinimumCannotAdd')}
                    </>
                )}
            </Box>
        );
    };

    const rightSecondary = (v) => {
        const tooCheap = isBelowMinimum(v, promotion.minimumAmount);
        const after = priceAfterPromotion(v.price, promotion);
        return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                {v.sku ? `${v.sku} · ` : ''}{formatCurrency(v.price)}
                {' → '}
                <strong>{after === null ? buyXGetYLabel : formatCurrency(after)}</strong>
                {tooCheap && (
                    <Tooltip title={t('promotions_ext.belowMinimum')}>
                        <WarningIcon color="warning" sx={{ fontSize: 16 }} />
                    </Tooltip>
                )}
            </Box>
        );
    };

    return (
        <Box>
            <Autocomplete
                options={products}
                getOptionLabel={(option) => `${option.name} (${option.sku || t('promotions_ext.noSku')})`}
                value={product}
                onChange={(_, newValue) => setProduct(newValue)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                    <TextField {...params} label={t('promotions_ext.pickProduct')} />
                )}
            />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    gap: 2,
                    mt: 2,
                }}
            >
                <TransferColumn
                    title={product ? t('promotions_ext.availableOf', { product: product.name }) : t('promotions_ext.available')}
                    items={left}
                    checked={checkedLeft}
                    onToggle={toggle(setCheckedLeft)}
                    onToggleAll={toggleAll(checkedLeft, setCheckedLeft, leftIds)}
                    renderSecondary={leftSecondary}
                    isDisabled={leftBlocked}
                    emptyText={product ? t('promotions_ext.noMoreVariants') : t('promotions_ext.pickProductFirst')}
                    showProduct={false}
                />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: 1 }}>
                    <Tooltip title={t('promotions_ext.addAllVariants')}>
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => moveRight(leftIds)}
                                disabled={leftIds.length === 0}
                                aria-label={t('promotions_ext.addAllVariants')}
                            >
                                ≫
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('promotions_ext.addChecked')}>
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => moveRight(leftChecked)}
                                disabled={leftChecked.length === 0}
                                aria-label={t('promotions_ext.addChecked')}
                            >
                                &gt;
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('promotions_ext.removeChecked')}>
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => moveLeft(rightChecked)}
                                disabled={rightChecked.length === 0}
                                aria-label={t('promotions_ext.removeChecked')}
                            >
                                &lt;
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('promotions_ext.removeAll')}>
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => moveLeft(rightIds)}
                                disabled={right.length === 0}
                                aria-label={t('promotions_ext.removeAll')}
                            >
                                ≪
                            </Button>
                        </span>
                    </Tooltip>
                </Box>

                <TransferColumn
                    title={t('promotions_ext.selectedVariants', { count: selectedVariants.length })}
                    items={right}
                    checked={checkedRight}
                    onToggle={toggle(setCheckedRight)}
                    onToggleAll={toggleAll(checkedRight, setCheckedRight, rightIds)}
                    renderSecondary={rightSecondary}
                    emptyText={t('promotions_ext.noVariantsSelected')}
                    showProduct
                />
            </Box>

            {error && selectedVariants.length === 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            )}

            {invalidCount > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    {t('promotions_ext.belowMinimumAlert', {
                        count: invalidCount,
                        min: formatCurrency(promotion.minimumAmount),
                    })}
                </Alert>
            )}
        </Box>
    );
};

export default PromotionVariantPicker;
